import { Box, Chip, Typography } from '@mui/material';
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  NullableBooleanInput,
  SelectInput,
  TextInput,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { formatMoney } from '../cashbacks/formatMoney';

/**
 * Cashouts list — the final Phase 1 read-only surface, where the payout-vs-
 * donation model (D6) becomes visible. Same proven pattern as Stores / Users /
 * Cashbacks: region-driven <List>/<Datagrid>, RequireAccess gating, custom nav.
 * List only — approve/reject (Phase 2) and the SLA dashboard (Phase 3) are not
 * here.
 *
 * A cashout row is a `cashout_requests` record joined (on the DataProvider
 * interface, never the seed) to the resolved MEMBER and, for donations, the
 * CHARITY name, by the provider's read-only `cashouts` resource.
 *
 * SECURITY: `payoutDetails` (an opaque encrypted envelope) is never surfaced —
 * the provider does not materialise it onto the row, and nothing here decodes or
 * logs it.
 *
 * Two model points made visible (D6):
 * - destinationType (where the money goes) is ORTHOGONAL to status (how far
 *   along). A payout shows its rail; a donation shows "Donation → <charity>".
 * - "Donated" is NOT a status — it is the DERIVED display state
 *   destinationType === 'donation' && status === 'paid', computed here for
 *   display only, never stored.
 */

// Friendly rail names (decision 1A). uk_paypal / us_paypal both read "PayPal".
const RAIL_LABEL: Record<string, string> = {
  uk_bank: 'UK Bank',
  uk_paypal: 'PayPal',
  us_ach: 'ACH',
  us_paypal: 'PayPal',
  us_venmo: 'Venmo',
};

// The progress statuses as visually distinct chips (failed/rejected read as
// error). `approved` is the admin→engine hand-off state (D-payout-boundary).
const STATUS_CHIP: Record<
  string,
  {
    label: string;
    color: 'info' | 'primary' | 'warning' | 'success' | 'error' | 'default';
  }
> = {
  requested: { label: 'Requested', color: 'info' },
  approved: { label: 'Approved', color: 'primary' },
  processing: { label: 'Processing', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  rejected: { label: 'Rejected', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

export const StatusChip = () => {
  const record = useRecordContext();
  const status = (record?.status as string) ?? '';
  // Derived display state: a settled donation reads "Donated" (D6), not "Paid".
  const donated = record?.destinationType === 'donation' && status === 'paid';
  if (donated) {
    return (
      <Chip size="small" label="Donated" color="secondary" variant="filled" />
    );
  }
  const chip = STATUS_CHIP[status] ?? { label: status, color: 'default' };
  return (
    <Chip
      size="small"
      label={chip.label}
      color={chip.color}
      variant={chip.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

// Member as a two-line cell: name (primary) + email (muted), same as Cashbacks.
export const MemberCell = () => {
  const record = useRecordContext();
  return (
    <Box>
      <Typography variant="body2">
        {(record?.memberName as string) ?? ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {(record?.memberEmail as string) ?? ''}
      </Typography>
    </Box>
  );
};

// One destination column (decision 1A): rail name for payouts, "Donation →
// <charity>" for donations.
export const DestinationCell = () => {
  const record = useRecordContext();
  if (record?.destinationType === 'donation') {
    const charity = (record?.charityName as string | null) ?? 'charity';
    return <span>{`Donation → ${charity}`}</span>;
  }
  const rail = (record?.payoutMethod as string) ?? '';
  return <span>{RAIL_LABEL[rail] ?? rail}</span>;
};

// Subtle red breach indicator (decision 2A) — nothing when not breached. The
// full SLA dashboard is Phase 3; this is just an at-a-glance flag.
export const SlaCell = () => {
  const record = useRecordContext();
  if (!record?.slaBreached) return null;
  return (
    <Chip size="small" label="SLA breached" color="error" variant="outlined" />
  );
};

// Region is driven by the switcher, NOT a column filter. `q` matches member
// name/email.
const cashoutFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'requested', name: 'Requested' },
      { id: 'processing', name: 'Processing' },
      { id: 'paid', name: 'Paid' },
      { id: 'failed', name: 'Failed' },
      { id: 'rejected', name: 'Rejected' },
      { id: 'cancelled', name: 'Cancelled' },
    ]}
  />,
  <SelectInput
    key="destinationType"
    source="destinationType"
    label="Destination"
    choices={[
      { id: 'payout', name: 'Payout' },
      { id: 'donation', name: 'Donation' },
    ]}
  />,
  <NullableBooleanInput
    key="slaBreached"
    source="slaBreached"
    label="SLA breached"
  />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const CashoutListView = () => {
  const { activeRegion } = useRegion();
  return (
    <List
      resource="cashouts"
      title="Cashouts"
      filter={{ region: activeRegion }}
      filters={cashoutFilters}
      sort={{ field: 'requestedAt', order: 'DESC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the cashout detail (where the approve/reject
          decision lives). No bulk actions. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Member"
          sortBy="memberName"
          render={() => <MemberCell />}
        />
        <FunctionField
          label="Amount"
          sortBy="amount"
          render={(record: RaRecord) =>
            formatMoney(record.amount, record.currency)
          }
        />
        <FunctionField
          label="Destination"
          sortBy="destinationType"
          render={() => <DestinationCell />}
        />
        <FunctionField
          label="Status"
          sortBy="status"
          render={() => <StatusChip />}
        />
        <FunctionField
          label="SLA"
          sortable={false}
          render={() => <SlaCell />}
        />
        <DateField source="requestedAt" label="Requested date" showTime />
      </Datagrid>
    </List>
  );
};

/**
 * Gated entry point registered as the `cashouts` Resource's list. Read-only, so
 * `viewer` (read everywhere) sees it alongside `super_admin`; editor / support /
 * fraud lack `cashouts:read` and never see the route or menu item.
 */
export const CashoutList = () => (
  <RequireAccess capability="cashouts:read">
    <CashoutListView />
  </RequireAccess>
);
